import { Container, Paper, Typography, Alert, AlertTitle } from "@mui/material";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import React, { useContext, useCallback, useEffect, useState, useMemo } from "react";
import _ from "lodash";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import Layout from "./components/Layout";
import ROUTES, { getUrlWithAccountId } from "./config/Routes";
import { Context } from "./states/Store";
import { theme } from "./style/globalStyle";
import { withTranslation } from "react-i18next";
import Dossiers from "./containers/dossiers/dossiers-list/Dossiers";
import DossierCreation from "./containers/dossiers/create-dossier/index";
import TPSearch from "./containers/creation-form/thirdparty-search";
import DossierSheet from "./containers/dossiers/dossier-sheet/DossierSheet";
import Documents from "./containers/account-documents/Documents";
import Thirdparties from "./containers/thirdparties/thirdparties-list/Thirdparties";
import Users from "./containers/users/users-list/Users";
import UserProfile from "./containers/users/user-profile/UserProfile";
import EADocViewer from "./containers/doc-viewer";
import TPSheet from "./containers/thirdparties/thirdparty-sheet/TPSheet";
import UnAuthorized from "./components/UnAuthorized.js";
import { LicenseInfo } from '@mui/x-license-pro';
import DashBoard from "./containers/dashboard/Dashboard";
import AdminSettings from "./components/admin-settings/AdminSettings";
import AdminSettingsEmbeddable from './components/admin-settings/AdminSettingsEmbeddable';
import EAButton from "./components/common/EAButton";
import i18n from "./i18n/i18n";
import { addLicense as am5AddLicence } from "@amcharts/amcharts5";
import Roles from "./containers/administration/roles/Roles";
import {
  RoleBasedAccessControlContext,
  getPrivilegeAscendants,
  NAVIGATION_REQUIRED_PRIVILEGES,
  RESOURCE_PRIVILEGES, PrivilegeEnum,
  PROPERTY_PRIVILEGES,
} from './services/common/RolesUtils';
import {useAuth} from "@ea-webapp/auth";
import { initRum } from "components/common/hooks/useDatadogRum";
import Events from "./containers/events/Events";
import Loader from "containers/loader/Loader";
import { getCurrentUser, manageRolesFromIdp, updateUserLanguage } from 'services/edge/UserService';
import { useAlert } from "components/common/hooks/useAlert";
import { datadogRum } from '@datadog/browser-rum';
import RoadMap from './containers/roadmap/RoadMap';
import { ERROR, UNAUTHORIZED_PAGE, SSO_PARAMETER_NAME, KEYCLOAK_REALM_PARAMETER_NAME } from './services/common/Constants';
import { PALETTE_COLORS } from './containers/administration/theme/themeUtils';
import { darken, lighten } from "@mui/material/styles";
import { getCurrentCompteDonneurDordresTheme } from './services/edge/AccountService';
import AddContext from './containers/Context/AddContext';
import AddThirdParty from './containers/Context/AddThirdParty';
import ContextComp from './containers/Context';
import i18next from 'i18next';
import AddTpContext from './containers/thirdparties/thirdparty-sheet/AddTpContext';
import { languages } from './components/EALanguageSelector';
import { getEnv } from "config/Config";
import AccountSuspended from "./containers/suspension/AccountSuspended";
import Reports from './containers/reports/Reports';
import { isBasicMode } from './config/AppMode';
import LightHome from './containers/light/LightHome';
import LightLaunch from './containers/light/LightLaunch';
import LightEvaluations from './containers/light/LightEvaluations';
import LightDossierDetail from './containers/light/LightDossierDetail';
import LightProfile from './containers/light/LightProfile';
import LightConsumption from './containers/light/LightConsumption';
import LightAccount from './containers/light/LightAccount';
import LightThirdparties from './containers/light/LightThirdparties';
import LightThirdpartyDetail from './containers/light/LightThirdpartyDetail';
import LightContexts from './containers/light/LightContexts';
import LightContextDetail from './containers/light/LightContextDetail';

// Redirect /:accountId/documents → return URL or dashboard in light mode
function LightDocumentsRedirect() {
  const [state] = React.useContext(Context);
  const history = useHistory();
  React.useEffect(() => {
    if (!state.account?.id) return;
    try {
      const raw = sessionStorage.getItem("light_form_return");
      if (raw) {
        sessionStorage.removeItem("light_form_return");
        const { returnUrl, reopenDoc } = JSON.parse(raw);
        if (returnUrl) {
          history.replace(returnUrl, { reopenDoc });
          return;
        }
      }
    } catch (_) {}
    history.replace(getUrlWithAccountId(state.account.id, ROUTES.DASHBOARD));
  }, [state.account?.id, history]);
  return null;
}

LicenseInfo.setLicenseKey(
  "937431cab6a61afd4f8f41cf81ca8ee4Tz03OTc4OSxFPTE3MzMwNDU0MTgwMDAsUz1wcmVtaXVtLExNPXBlcnBldHVhbCxLVj0y"
);

//chart licence
am5AddLicence("AM5C374640337");
//map licence
am5AddLicence("AM5M374640337");


export const initLocalize = async (userLang, dispatch) => {
    window.Localize.initialize({
        key: getEnv("REACT_APP_LOCALIZE_ID"),
        rememberLanguage: true,
        targetLanguage: userLang,
        translateBody: false,
        translateMetaTags: false
    });
}


// Use this to translate with localize in JS when needed
export const localizeTranslate = (text) => {
    return window.Localize?.translate(text) || text;
}

const AppRoute = ({
  home,
  path,
  component: Component,
  layout: Layout,
  variant,
  crumbs,
  isAuthorized,
  ...rest
}) => (
  <Route
    {...rest}
    path={path}
    render={(props) => (
      <Layout variant={variant} crumbs={crumbs} isAuthorized={isAuthorized}>
        <Component {...props} {...rest} />
      </Layout>
    )}
  />
);

// TODO: Change the no match page !
function NoMatch({ home }) {
  let location = useLocation();
  let history = useHistory();

  return (
    <Layout home={home}>
      <Container style={{ padding: "4em", margin: "0 auto", width: "50%" }}>
        <Paper>
          <Alert severity="warning">
            <AlertTitle>Chemin introuvable</AlertTitle>
            <p>Le chemin {location.pathname} n'existe pas !</p>
            <Typography variant="inherit">
              <EAButton
                onClick={() => history.push("/")}
                content={i18n.t("goToHome")}
              />
            </Typography>
          </Alert>
        </Paper>
      </Container>
    </Layout>
  );
}

const getPathWithAccount = (path) => `/:accountId/${path}`;

function App() {
  const [state, dispatch] = useContext(Context);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [isSigUser, setIsSigUser] = useState(false);
  const [userLang, setUserLang] = useState();
  const [userTheme, setUserTheme] = useState(theme);
  const { authState, loginKeycloak } = useAuth();
  const { onError } = useAlert();

  const isEffectiveGlobalAdmin = useMemo(() => 
    isGlobalAdmin && state.superAdminConnectedAsRoles?.length === 0
  , [isGlobalAdmin, state.superAdminConnectedAsRoles?.length] ) 

  // Override le theme par le theme personalisé du compte
  useEffect(() => {

    // Définis la couleur de surbrillance du texte
    if (state.personalization.isCustomTemplate){
      const selectionColor = state.personalization.colorHover || "#54CC54";

      let styleElement = document.getElementById('selectionColorStyle');

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'selectionColorStyle';
        document.head.appendChild(styleElement);
      }

      styleElement.innerText = `
        ::selection {
          background: ${selectionColor};
          color: white;
        }
        ::-moz-selection {
          background: ${selectionColor};
          color: white;
        }`;
  }

      if (state.personalization.isCustomTemplate && !state.personalization.isCustomColor){
        setUserTheme({
          ...theme,
          palette: {
            ...theme.palette,
            primary: {
              ...theme.palette.primary,
              primary0 :  _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[50] || "#EFFBF1",
              primary50:  _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[50]  || "#EFFBF1",
              primary100: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[100] || "#F6F9F3",
              primary200: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[200] || "#D7F4DC",
              primary300: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[300] || "#B0E8B9",
              primary400: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[400] || "#60D273",
              primary500: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[500] || "#60D273",
              main:       _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[500] || "#60D273",
              primary600: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[600] || "#2D9F40",
              primary700: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[800] || "#227730",
              primary800: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[900] || "#32614F",
              primary900: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[900] || "#18260D",
            },
          },
          components: {
            ...theme.components,
            MuiMenuItem: {
              styleOverrides: {
                root: {
                  "&:hover": {
                    backgroundColor: _.find(PALETTE_COLORS, { name: state.personalization.paletteColorName || "aprovall" })[100] || "#F6F9F3",
                  },
                },
              }
            },
          }
        })
      }else if(state.personalization.isCustomTemplate && state.personalization.colorPrimary){
        const primary = state.personalization.colorPrimary;
        setUserTheme({
          ...theme,
          palette: {
            ...theme.palette,
            primary: {
              ...theme.palette.primary,
              primary0: state.personalization.colorBackground,
              primary50: lighten(primary, 0.9) || "#F6F9F3",
              primary100: lighten(primary, 0.8) || "#F6F9F3",
              primary200: lighten(primary, 0.7) || "#D7F4DC",
              primary300: lighten(primary, 0.6) || "#B0E8B9",
              primary400: lighten(primary, 0.5) || "#60D273",
              primary500: lighten(primary, 0.4) || "#60D273",
              main:       lighten(primary, 0.4) || "#60D273",
              primary600: darken(primary, 0,6) || "#2D9F40",
              primary700: darken(primary, 0,7) || "#227730",
              primary800: darken(primary, 0,8) || "#32614F",
              primary900: darken(primary, 0,9) || "#18260D",
            },
          },
          components: {
            ...theme.components,
            MuiMenuItem: {
              styleOverrides: {
                root: {
                  "&:hover": {
                    backgroundColor: lighten(primary, 0.8) || "#F6F9F3",
                  },
                },
              }
            },
          }
        })
      } else {
        setUserTheme(theme);
      }
  }, [state.personalization]);

  useEffect(() => {
    initRum();

    const queryParameters = new URLSearchParams(window.location.search)
    const ssoAccountName = queryParameters.get(SSO_PARAMETER_NAME)
    const keycloakRealm = queryParameters.get(KEYCLOAK_REALM_PARAMETER_NAME)
    loginKeycloak(ssoAccountName, keycloakRealm, manageRolesFromIdp, onError);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const initLocalization = async () => {
      await i18next.changeLanguage(userLang);
      await updateUserLanguage(userLang);

      // Redéfinis la balise lang, ne traduit pas si langue navigateur = langue sélectionnée
      document.documentElement.lang = userLang;
      let userNavigatorLang = navigator.language.substring(0, 2);
      if (userNavigatorLang === userLang){
        document.documentElement.classList.add('notranslate');
      }
      localStorage.setItem('i18nextLng', userLang);
      dispatch({ type: "LANG", lang: userLang })
    }

    if (userLang) {
      initLocalization();
      initLocalize(userLang, dispatch);
    }
  }, [dispatch, userLang, isSigUser]);

  useEffect(() => {
    if (state.account && authState.keycloak) {

      window.StonlyWidget('sendData', {
        contactForm: {
          email: authState.keycloak.tokenParsed.email,
          additionalInfo: {
            origine: "365",
            nom: authState.keycloak.tokenParsed.family_name,
            prenom: authState.keycloak.tokenParsed.given_name,
            email: authState.keycloak.tokenParsed.email,
            role: isEffectiveGlobalAdmin ? "SUPER_ADMIN" : state.roles.map(r => r.name).toString(),
            compte: state.account.account,
            navigateur: navigator.userAgent
          }
        }
      });
      if (state.lang) {
        window.StonlyWidget('setWidgetLanguage', state.lang);
      }
    }
  }, [state.roles, state.account, state.lang, authState.keycloak, isEffectiveGlobalAdmin]);

  useEffect(() => {
    async function getCurrentUserInfo() {
      let result = await getCurrentUser();
      setIsGlobalAdmin(result.content.superAdmin);
      dispatch({ type: "SUPER_ADMIN_USER", isSuperAdminUser: result.content.superAdmin })
      let userNavigatorLang = navigator.language.substring(0, 2);
      setUserLang(result.content.lang || (languages.map(lang => lang.value).includes(userNavigatorLang) ? userNavigatorLang : "en"))
    }
    if(authState.user?.azp === getEnv("REACT_APP_SIG_KEYCLOAK_CLIENT")) {
      setUserLang(localStorage.getItem('i18nextLng') ?? "fr")
      setIsSigUser(true);
    }
    if (authState.user?.id) {
      getCurrentUserInfo()

      datadogRum.setUser({
        id: authState.user.id,
        email: authState.user.email
      })

    }
  }, [authState.user, dispatch]);

  // Récupère le theme du compte
  useEffect(() => {
    async function getCurrentPersonalization() {
      try {
        let result = await getCurrentCompteDonneurDordresTheme(state.account.id, null);

        if (result?.content) {
          dispatch({ type: "PERSONALIZATION", data: result.content })
        }else{
          dispatch({ type: "PERSONALIZATION", data: {isCustomTemplate: false, noFindingTheme: true} })
        }
      } catch (e) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: "INTERNAL_ERROR" },
        });
      }
    }

    if (state.account) {
      getCurrentPersonalization()
    }
  }, [state.account, dispatch]);

  // For non scoped resources: this makes checks against all user roles
  const hasAnyOfPrivileges = useCallback((...privileges) => {
    let bHasAnyOfPrivileges = false;
    if (isEffectiveGlobalAdmin) {
      bHasAnyOfPrivileges = true;
    } else {
      let involvedRoles = state.roles || [];
      if (!_.isEmpty(involvedRoles)) {
        bHasAnyOfPrivileges = involvedRoles.some(ownedRole => {
          let grantedPrivilegesNames = ownedRole?.flatPrivileges?.map(p => p.name)
          return privileges.some(priv => {
            return grantedPrivilegesNames.includes(priv.name) ||
              [...getPrivilegeAscendants(priv)].some(asc => {
                return grantedPrivilegesNames.includes(asc)
              });
          })
        })
      }
    }
    return bHasAnyOfPrivileges;
  }, [isEffectiveGlobalAdmin, state.roles]);

  const hasAnyOfPrivilegesOnScopedResourceImpl = useCallback((scopedResource, ...privileges) => {
    let bHasAnyOfPrivileges = false;
    if (isEffectiveGlobalAdmin) {
      bHasAnyOfPrivileges = true;
    } else {
      const scopedResourceAuthorizingRoles = scopedResource?.authorizingRoles || [];

      if (!_.isEmpty(state.roles)) {
        // Pick up roles from state.roles using their ids
        const involvedRoles = state.roles.filter(r => scopedResourceAuthorizingRoles.includes(r.id));

        if (!_.isEmpty(involvedRoles)) {
          bHasAnyOfPrivileges = involvedRoles.some(ownedRole => {
            let grantedPrivileges = ownedRole.flatPrivileges;
            let grantedPrivilegesNames = ownedRole.flatPrivileges.map(p => p.name)

            return privileges.some(priv => {
              // Verify if the privilege is specific to a resource
              const specificPrivilege = RESOURCE_PRIVILEGES.find(sp => sp.privilege === priv.name);

              // Get the id of the resource if the privilege is specific to a resource
              let id = null;
              if (specificPrivilege) {
                switch (specificPrivilege.type) {
                  case 'DOCUMENT':
                    id = scopedResource.typeDocumentId;
                    break;
                  case 'DOSSIER':
                    id = scopedResource.dossierId;
                    break;
                  case 'THIRDPARTY':
                    id = scopedResource.thirdpartyId;
                    break;
                  default:
                    break;
                }
              }

              // Verify if the privilege is granted on the resource
              const isPrivilegeGrantedOnResource = !specificPrivilege ||
                (specificPrivilege && grantedPrivileges?.some(p => p?.name === priv?.name && p?.resources?.includes(id)));

              return (isPrivilegeGrantedOnResource) &&
                (grantedPrivilegesNames?.includes(priv.name) || [...getPrivilegeAscendants(priv)].some(asc => {
                  return grantedPrivilegesNames?.includes(asc)
                }))
            })
          })
        }
      }
    }
    return bHasAnyOfPrivileges;

  }, [isEffectiveGlobalAdmin, state.roles]);

  const hasAnyOfPrivilegesOnPropertyId = useCallback((propertyId, ...privileges) => {
    let bHasAnyOfPrivileges = false;
    if (isEffectiveGlobalAdmin) {
      bHasAnyOfPrivileges = true;
    } else if (!_.isEmpty(state.roles)) {

      bHasAnyOfPrivileges = state.roles.some(ownedRole => {
        let grantedPrivileges = ownedRole.flatPrivileges;
        let grantedPrivilegesNames = ownedRole.flatPrivileges.map(p => p.name)
        return privileges.some(priv => {

          // Verify if the privilege can be specific to a property id
          const isPropertyPrivilege = PROPERTY_PRIVILEGES.some(sp => sp.privilege === priv.name);

          const hasAnyOfPrivilegesParentsGranted = [...getPrivilegeAscendants(priv)].some(asc => {
              return grantedPrivilegesNames.includes(asc)
            })
          if(hasAnyOfPrivilegesParentsGranted) {
            //Si le rôle à l'un des privileges parents, return true
            return true;
          } else if(isPropertyPrivilege && grantedPrivilegesNames.includes(priv.name)) {
            // vérifier si le privilege contient le propertyId fourni
            const propertiesCriteria = grantedPrivileges?.find(p => p.name === priv.name)?.propertiesCriteria;
            return propertiesCriteria ? Object.keys(propertiesCriteria)?.includes(propertyId.toString()) : false;
          }
          return false;
        })
      })

    }

    return bHasAnyOfPrivileges;
  }, [isEffectiveGlobalAdmin, state.roles])

  const rbacContext = useMemo(
    () => ({
      hasAnyOfPrivileges: hasAnyOfPrivileges,
      hasAnyOfPrivilegesOnScopedResourceImpl: hasAnyOfPrivilegesOnScopedResourceImpl,
      hasAnyOfPrivilegesOnPropertyId: hasAnyOfPrivilegesOnPropertyId,
      isGlobalAdmin: isEffectiveGlobalAdmin,
      isSigUser: isSigUser
    }),
    [hasAnyOfPrivileges, hasAnyOfPrivilegesOnScopedResourceImpl, hasAnyOfPrivilegesOnPropertyId, isEffectiveGlobalAdmin, isSigUser]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={userTheme}>
        <ScopedCssBaseline>
          <RoleBasedAccessControlContext.Provider value={rbacContext}>
            <Router>
              <Switch>
                {/* Décommentez pour pouvoir accéder à la page qui liste les icônes */}
                {/*{isGlobalAdmin &&*/}
                {/*  <AppRoute*/}
                {/*    exact*/}
                {/*    path={getPathWithAccount(ROUTES.ICONS_VIEWER)}*/}
                {/*    layout={Layout}*/}
                {/*    active*/}
                {/*    component={IconsViewer}*/}
                {/*    crumbs={{ data: [] }}*/}
                {/*  />*/}
                {/*}*/}
                <AppRoute
                  exact
                  path={[`/${ROUTES.DEFAULT}`, getPathWithAccount(ROUTES.DASHBOARD)]}
                  layout={Layout}
                  active
                  component={isBasicMode() ? LightHome : DashBoard}
                  crumbs={{ data: [] }}
                />
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_LAUNCH)}
                    layout={Layout}
                    component={LightLaunch}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_EVALUATIONS)}
                    layout={Layout}
                    component={LightEvaluations}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_DOSSIER_DETAIL)}
                    layout={Layout}
                    component={LightDossierDetail}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_ACCOUNT)}
                    layout={Layout}
                    component={LightAccount}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_CONSUMPTION)}
                    layout={Layout}
                    component={LightConsumption}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_THIRDPARTY_DETAIL)}
                    layout={Layout}
                    component={LightThirdpartyDetail}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_THIRDPARTIES)}
                    layout={Layout}
                    component={LightThirdparties}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_CONTEXT_DETAIL)}
                    layout={Layout}
                    component={LightContextDetail}
                    crumbs={{ data: [] }}
                  />
                )}
                {isBasicMode() && (
                  <AppRoute
                    exact
                    path={getPathWithAccount(ROUTES.LIGHT_CONTEXTS)}
                    layout={Layout}
                    component={LightContexts}
                    crumbs={{ data: [] }}
                  />
                )}
                <AppRoute
                  exact
                  path={[`/${ROUTES.ANSWER}`]}
                  // no layout
                  layout={Layout}
                  active
                  component={Loader}
                  crumbs={{ data: [] }}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.DOSSIERS)}
                  layout={Layout}
                  variant={"list"}
                  active
                  component={Dossiers}
                  crumbs={{ data: [{ label: "breadcrumb.dossiers" }] }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ADMIN)}
                  layout={Layout}
                  component={AdminSettings}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.admin)}
                  variant={"withoutAppBar"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ADMIN_EMBEDDABLE)}
                  layout={Layout}
                  component={AdminSettingsEmbeddable}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.admin)}
                  variant={"embeddable"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ROADMAP)}
                  layout={Layout}
                  component={RoadMap}
                  variant={"withoutAppBar"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.DOSSIERS)}
                  layout={Layout}
                  variant={"list"}
                  active={false}
                  component={Dossiers}
                  crumbs={{
                    data: [
                      { label: "breadcrumb.archives" },
                      { label: "breadcrumb.dossiers" },
                    ],
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.TP_ADD_DOSSIER)}
                  layout={Layout}
                  component={DossierCreation}
                  variant={"grey"}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.thirdparties",
                        path: ROUTES.THIRDPARTIES,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.DOSSIER_DETAIL)}
                  layout={Layout}
                  component={DossierSheet}
                  active
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.dossiers",
                        path: ROUTES.DOSSIERS,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.DOSSIER_DOCUMENT_DETAIL)}
                  layout={Layout}
                  component={EADocViewer}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.dossiers",
                        path: ROUTES.DOSSIERS,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.DOSSIER_DOCUMENT_DETAIL)}
                  layout={Layout}
                  component={EADocViewer}
                  crumbs={{
                    data: [
                      { label: "breadcrumb.archives" },
                      {
                        label: "breadcrumb.dossiers",
                        path: ROUTES.ARCHIVES.DOSSIERS,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.DOSSIER_DETAIL)}
                  layout={Layout}
                  component={DossierSheet}
                  active={false}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.archives"
                      },
                      {
                        label: "breadcrumb.dossiers",
                        path: ROUTES.ARCHIVES.DOSSIERS
                      }
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTIES)}
                  layout={Layout}
                  variant={"list"}
                  active
                  component={Thirdparties}
                  crumbs={{ data: [{ label: "breadcrumb.thirdparties" }] }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.thirdparty)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.THIRDPARTIES)}
                  layout={Layout}
                  variant={"list"}
                  active={false}
                  component={Thirdparties}
                  crumbs={{
                    data: [
                      { label: "breadcrumb.archives" },
                      { label: "breadcrumb.thirdparties" },
                    ],
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.thirdparty)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTY_ADD)}
                  layout={Layout}
                  component={TPSearch}
                  variant={"grey"}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.thirdparties",
                        path: ROUTES.THIRDPARTIES,
                      },
                      { label: "breadcrumb.addThirdparty" },
                    ],
                  }}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.EVENTS)}
                  layout={Layout}
                  component={Events}
                  crumbs={{ data: [{ label: "breadcrumb.events" }] }}
                  isAuthorized={isEffectiveGlobalAdmin}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTY_DETAIL)}
                  layout={Layout}
                  variant={"noPadding"}
                  active
                  component={TPSheet}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.thirdparties",
                        path: ROUTES.THIRDPARTIES
                      }
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.thirdparty)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTY_DOSSIER_DETAIL)}
                  layout={Layout}
                  component={DossierSheet}
                  active
                  crumbs={{
                    data: [],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTY_DOCUMENT_DETAIL)}
                  layout={Layout}
                  component={EADocViewer}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.thirdparties",
                        path: ROUTES.THIRDPARTIES,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.THIRDPARTY_DOSSIER_DOCUMENT_DETAIL)}
                  layout={Layout}
                  component={EADocViewer}
                  crumbs={{
                    data: [],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.THIRDPARTY_DOSSIER_DOCUMENT_DETAIL)}
                  layout={Layout}
                  component={EADocViewer}
                  crumbs={{
                    data: [],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.THIRDPARTY_DETAIL)}
                  layout={Layout}
                  active={false}
                  component={TPSheet}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.archives"
                      },
                      {
                        label: "breadcrumb.thirdparties",
                        path: ROUTES.ARCHIVES.THIRDPARTIES
                      }
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.thirdparty)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.THIRDPARTY_DOSSIER_DETAIL)}
                  layout={Layout}
                  component={DossierSheet}
                  active
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.archives"
                      }
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.DOCUMENTS)}
                  layout={Layout}
                  variant={"list"}
                  component={isBasicMode() ? LightDocumentsRedirect : Documents}
                  crumbs={{ data: [{ label: "breadcrumb.documents" }] }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.PROFILE)}
                  layout={Layout}
                  component={isBasicMode() ? LightProfile : UserProfile}
                  variant={"withoutAppBar"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.USERS)}
                  layout={Layout}
                  component={Users}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.user)}
                  variant={"withoutAppBar"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ROLES)}
                  layout={Layout}
                  component={Roles}
                  isAuthorized={ hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.role) }
                  variant={"withoutAppBar"}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.REPORTS)}
                  layout={Layout}
                  component={Reports}
                  variant={"list"}
                  crumbs={{ data: [{ label: "breadcrumb.exports" }] }}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.DOCUMENT_DETAIL)}
                  layout={Layout}
                  variant={"list"}
                  component={EADocViewer}
                  crumbs={{
                    data: [
                      {
                        label: "breadcrumb.documents",
                        path: ROUTES.DOCUMENTS,
                      },
                    ],
                    hasAdditionalData: true,
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.document)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.CONTEXT)}
                  layout={Layout}
                  active
                  variant={"list"}
                  component={ContextComp}
                  crumbs={{data: [{label: "breadcrumb.context"}]}}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.context)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ADD_CONTEXT)}
                  layout={Layout}
                  active
                  component={AddContext}
                  crumbs={{
                    data: [
                      {label: "breadcrumb.context"},
                      {label: "context.creation"},
                    ]
                  }}
                  isAuthorized={hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.TP_SHEET_ADD_TP)}
                  layout={Layout}
                  active
                  component={AddTpContext}
                  crumbs={{
                    data: [
                      {label: "breadcrumb.context"},
                      {label: "context.creation"},
                    ]
                  }}
                  isAuthorized={hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(`${ROUTES.CONTEXT_DETAIL_ADD_TP}`)}
                  layout={Layout}
                  active
                  component={AddThirdParty}
                  crumbs={{data: [{label: "breadcrumb.context"}]}}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.dossier)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ARCHIVES.CONTEXTS)}
                  layout={Layout}
                  active={false}
                  component={ContextComp}
                  variant={"list"}
                  crumbs={{
                    data: [
                      {label: "breadcrumb.archives"},
                      {label: "breadcrumb.context"},
                    ],
                  }}
                  isAuthorized={hasAnyOfPrivileges(...NAVIGATION_REQUIRED_PRIVILEGES.context)}
                />
                <AppRoute
                  exact
                  path={getPathWithAccount(ROUTES.ACCOUNT_SUSPENDED)}
                  layout={Layout}
                  active
                  component={AccountSuspended}
                  variant={"withoutAppBar"}
                />
                <Route path={"/" + UNAUTHORIZED_PAGE}>
                  <UnAuthorized />
                </Route>
                <Route path="*">
                  <NoMatch />
                </Route>
              </Switch>
            </Router>
          </RoleBasedAccessControlContext.Provider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default withTranslation()(App);
